/**
 * IncomeLinkingSheet — Link an ART_INCOME transaction to an unsold artwork
 *
 * Shows unsold verdicts sorted by amount proximity to the transaction.
 * On selection, marks artwork as sold and links the transaction.
 */

import { useMemo } from 'react';
import { T } from '@/styles/tokens';
import { BottomSheet } from '@/components/ui/BottomSheet';
import type { ArtTransaction } from '@/hooks/useArtTransactions';
import type { Verdict } from '@/types';
import { MEDIUMS } from '@/types';

interface IncomeLinkingSheetProps {
  transaction: ArtTransaction | null;
  isOpen: boolean;
  onClose: () => void;
  verdicts: Verdict[];
  onLink: (verdictId: string) => Promise<void>;
  onDeclineLink?: (transactionId: string) => Promise<void>;
}

export function IncomeLinkingSheet({ transaction, isOpen, onClose, verdicts, onLink, onDeclineLink }: IncomeLinkingSheetProps) {
  // Filter to unsold, sort by amount proximity
  const unsold = useMemo(() => {
    if (!transaction) return [];
    const txnAmount = Math.abs(transaction.amount);
    return verdicts
      .filter(v => !v.sold)
      .sort((a, b) => {
        const diffA = Math.abs((a.inputs.gutPrice ?? a.calculations.ttcp) - txnAmount);
        const diffB = Math.abs((b.inputs.gutPrice ?? b.calculations.ttcp) - txnAmount);
        return diffA - diffB;
      });
  }, [verdicts, transaction]);

  if (!transaction) return null;

  const amountDollars = Math.round(Math.abs(transaction.amount) / 100);

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <div style={{ padding: '8px 20px 20px' }}>
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 4 }}>
            Match to artwork
          </div>
          <div style={{ fontSize: 14, color: T.textSecondary }}>
            {transaction.merchantName} · ${amountDollars.toLocaleString()} · {transaction.date}
          </div>
        </div>

        {/* Verdict list */}
        {unsold.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '45vh', overflowY: 'auto' }}>
            {unsold.map((v) => {
              const title = v.inputs.title || `${v.inputs.width}\u00D7${v.inputs.height}"`;
              const mediumLabel = MEDIUMS[v.inputs.medium]?.label || v.inputs.medium;
              const ttcpDollars = Math.round(v.calculations.ttcp / 100);
              return (
                <button
                  key={v.id}
                  onClick={() => onLink(v.id)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: 12,
                    border: `1px solid ${T.border}`,
                    background: T.white,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 150ms',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontSize: 14, fontWeight: 600, color: T.text,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {title}
                    </div>
                    <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 2 }}>
                      {mediumLabel} · {v.inputs.width}\u00D7{v.inputs.height}"
                    </div>
                  </div>
                  <div style={{
                    fontSize: 13, fontWeight: 600, color: T.primary,
                    whiteSpace: 'nowrap', flexShrink: 0,
                  }}>
                    True Price ${ttcpDollars.toLocaleString()}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px 0', color: T.textSecondary, fontSize: 14 }}>
            No unsold pieces to link.
          </div>
        )}

        {/* "or" divider */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0 12px',
        }}>
          <div style={{ flex: 1, height: 1, background: T.border }} />
          <span style={{ fontSize: 12, color: T.textSecondary }}>or</span>
          <div style={{ flex: 1, height: 1, background: T.border }} />
        </div>

        {/* "Not for a specific piece" — sets tpLinkDeclined */}
        <button
          onClick={async () => {
            if (onDeclineLink && transaction) {
              await onDeclineLink(transaction.id);
            }
            onClose();
          }}
          style={{
            width: '100%',
            padding: '16px',
            borderRadius: 12,
            border: `1px dashed ${T.border}`,
            background: T.white,
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 500, color: T.text, fontFamily: T.fontFamily }}>
            Not for a specific piece
          </div>
          <div style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.fontFamily, marginTop: 2 }}>
            Teaching, licensing, etc.
          </div>
        </button>
      </div>
    </BottomSheet>
  );
}

export default IncomeLinkingSheet;
